import React, { useState } from "react";
import { C } from "../../reporting/reportingStyles";
import { LABELS, LOGO_WIDTH_BY_SIZE, QR_MODULE_BY_SIZE } from "./receiptLayoutMeta";

// Printable width in dots and on-screen width in px. Sizes follow the screen's Epson/bitmap scale
// (signage src/config/printers.ts EPSON_FONT_PX), scaled to the preview, so relative sizes match
// what comes out of the printer. It is an approximation of the paper, not a pixel copy.
const PAPER = {
    "80mm": { dots: 576, px: 300 },
    "58mm": { dots: 384, px: 220 },
};
const FONT_PX = { xs: 15, sm: 18, md: 21, lg: 26, xl: 34 };
const CALL_NO_PX = 56;

const splitLines = (value) =>
    String(value || "")
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);

// Fixed sample order, so the whole paper is visible while the header and footer are edited.
const SAMPLE = {
    branch: { name: "Main branch", address: "King Fahd Rd", arabicAddress: "طريق الملك فهد", phone: "0110000000" },
    placedAt: "2026-09-28 14:22",
    orderId: "184521",
    orderType: "Pickup",
    lines: [
        // Unit prices, as the POS prints them: the product without its modifiers, each modifier on
        // its own row, and nothing for a free one.
        {
            name: "Latte", arabicName: "لاتيه", qty: 2, total: "18.00 SAR",
            modifiers: [
                { name: "Extra shot", arabicName: "شوت إضافي", qty: 2, price: "4.00 SAR" },
                { name: "Oat milk", arabicName: "حليب الشوفان", qty: 2, price: null },
            ],
            note: "no sugar",
        },
        { name: "Croissant", arabicName: "كرواسون", qty: 1, total: "12.00 SAR", modifiers: [], note: null },
    ],
};

/**
 * A receipt-shaped preview of a layout, filled with the company's real details and a sample order.
 *
 * Without it nobody can predict the printed output and the editor becomes trial and error with a
 * paper roll. Mirrors signage src/utils/print/receiptDoc.ts `buildReceiptBlocks` — if that changes
 * what a row prints, change it here too.
 */
const ReceiptPreview = ({ layout, company, logoUrl }) => {
    const [paper, setPaper] = useState("80mm");
    if (!layout) return null;

    const { dots, px } = PAPER[paper];
    const scale = px / dots;
    const font = (size) => Math.max(8, Math.round(FONT_PX[size] * scale * 10) / 10);
    const compliant = !!company.vatNumber;
    const prefix = layout.callNumber?.prefix || "";
    const callNo = prefix ? `${prefix}-0042` : "0042";

    const textStyle = (row) => ({
        fontSize: font(row.size),
        fontWeight: row.bold ? 700 : 400,
        textAlign: row.align,
        lineHeight: 1.3,
        wordBreak: "break-word",
    });
    const kv = (label, value, size = "sm", bold = false) => (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: font(size), fontWeight: bold ? 700 : 400 }}>
            <span>{label}</span>
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
        </div>
    );
    const divider = <div style={{ borderTop: "1px dashed #333", margin: "6px 0" }} />;

    const rowLines = (row) => {
        switch (row.key) {
            case "brandName":
                return [company.name];
            case "arabicName":
                return company.arabicName && company.arabicName !== company.name ? [company.arabicName] : [];
            case "invoiceTitle":
                return compliant
                    ? [LABELS.taxInvoiceEn, LABELS.taxInvoiceAr]
                    : [LABELS.orderReceiptEn, LABELS.orderReceiptAr];
            case "vatNumber":
                return company.vatNumber ? [`${LABELS.vatNumber}: ${company.vatNumber}`] : [];
            case "crNumber":
                return company.companyRegistery ? [`${LABELS.crNumber}: ${company.companyRegistery}`] : [];
            case "address":
                return splitLines(company.address);
            case "branchName":
                return [`${LABELS.branch}: ${SAMPLE.branch.name}`];
            case "branchAddress":
                return [SAMPLE.branch.address, SAMPLE.branch.arabicAddress];
            case "branchPhone":
                return [SAMPLE.branch.phone];
            case "phone":
                return [company.phone];
            case "website":
                return [company.website];
            case "email":
                return [company.email];
            case "text":
                return [row.en, row.ar];
            default:
                return [];
        }
    };

    const renderRow = (row, i) => {
        if (!row.visible) return null;
        if (row.key === "logo") {
            return logoUrl ? (
                <div key={i} style={{ textAlign: "center", margin: "2px 0 4px" }}>
                    <img
                        src={logoUrl}
                        alt=""
                        style={{
                            width: Math.round((LOGO_WIDTH_BY_SIZE[row.size] || LOGO_WIDTH_BY_SIZE.lg) * scale),
                            filter: "grayscale(1) contrast(1.4)",
                        }}
                    />
                </div>
            ) : null;
        }
        if (row.key === "zatcaQr") {
            // A ZATCA QR is ~50 modules across; each module is this many dots.
            const qrPx = Math.round(50 * (QR_MODULE_BY_SIZE[row.size] || QR_MODULE_BY_SIZE.md) * scale);
            return compliant ? (
                <div key={i} style={{ display: "flex", justifyContent: "center", margin: "6px 0" }}>
                    <div
                        style={{
                            width: qrPx,
                            height: qrPx,
                            border: "2px solid #111",
                            background:
                                "repeating-linear-gradient(45deg,#111 0 3px,#fff 3px 6px),repeating-linear-gradient(-45deg,#111 0 3px,transparent 3px 6px)",
                            opacity: 0.8,
                        }}
                        title="ZATCA QR code"
                    />
                </div>
            ) : null;
        }
        const lines = rowLines(row).filter(Boolean);
        if (!lines.length) return null;
        return (
            <div key={i} style={textStyle(row)}>
                {lines.map((l, j) => (
                    <div key={j}>{l}</div>
                ))}
            </div>
        );
    };

    const items = layout.items || {};
    const itemFont = font(items.size || "sm");

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: C.textLight }}>Paper width</div>
                <div style={{ display: "flex", gap: 4 }}>
                    {Object.keys(PAPER).map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPaper(p)}
                            style={{
                                fontSize: 11,
                                padding: "3px 8px",
                                borderRadius: 6,
                                border: `1px solid ${paper === p ? C.coral : C.border}`,
                                background: paper === p ? C.coralLight : C.white,
                                color: paper === p ? C.coralDark : C.textMed,
                                cursor: "pointer",
                            }}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ background: C.bg, borderRadius: 10, padding: 14, display: "flex", justifyContent: "center" }}>
                <div
                    style={{
                        width: px,
                        background: "#fff",
                        color: "#111",
                        padding: "14px 10px 18px",
                        boxShadow: C.shadowMd,
                        fontFamily: "'Courier New', monospace",
                    }}
                >
                    {layout.header.map(renderRow)}
                    <div style={{ textAlign: "center", fontSize: Math.round(CALL_NO_PX * scale), fontWeight: 700, lineHeight: 1.1, marginTop: 6 }}>
                        {callNo}
                    </div>
                    {divider}
                    <div style={{ textAlign: "right", fontSize: font("sm") }}>{SAMPLE.placedAt}</div>
                    {kv(LABELS.orderRef, SAMPLE.orderId)}
                    {kv(LABELS.orderType, SAMPLE.orderType)}
                    {divider}
                    {SAMPLE.lines.map((l, i) => (
                        <div key={i} style={{ fontSize: itemFont, marginBottom: 2 }}>
                            <div style={{ display: "flex", gap: 6 }}>
                                <span style={{ flex: 1 }}>
                                    {items.showArabicName ? `${l.name} ${l.arabicName}` : l.name}
                                </span>
                                <span>x{l.qty}</span>
                                <span style={{ whiteSpace: "nowrap" }}>{l.total}</span>
                            </div>
                            {items.showModifiers && l.modifiers.length > 0 && (
                                <>
                                    <div>&nbsp;&nbsp;{LABELS.addOns}</div>
                                    {l.modifiers.map((m, j) => (
                                        <div key={j} style={{ display: "flex", gap: 6 }}>
                                            <span style={{ flex: 1 }}>
                                                &nbsp;&nbsp;{m.name}{items.showArabicName ? ` (${m.arabicName})` : ""}
                                            </span>
                                            <span>x{m.qty}</span>
                                            <span style={{ whiteSpace: "nowrap", minWidth: 62, textAlign: "right" }}>{m.price || ""}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                            {items.showNote && l.note && <div>&nbsp;&nbsp;* {l.note}</div>}
                        </div>
                    ))}
                    {divider}
                    {kv(LABELS.amount, "48.70 SAR")}
                    {kv(LABELS.vat, "7.30 SAR")}
                    {divider}
                    {kv(LABELS.total, "56.00 SAR", "md", true)}
                    {kv("MADA", "56.00 SAR")}
                    {divider}
                    {layout.footer.map(renderRow)}
                </div>
            </div>
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>
                Sample order and branch. Company details and logo are this merchant&apos;s.
                {!compliant && " With no VAT number the receipt prints as an Order Receipt, without a QR code."}
            </div>
        </div>
    );
};

export default ReceiptPreview;
